import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { TrainingVideo } from '@/types/videoAnnotations';
import { rollReviewRepository } from '../repositories/supabaseRollReviewRepository';
import { rollReviewApi } from '../services/rollReviewApi';
import {
  ACCEPTANCE_WINDOW_MS,
  acceptanceDeadline,
  deliveryDeadline,
} from '../utils/reviewWindows';

// Re-export so existing imports from this file keep working
export { acceptanceDeadline, deliveryDeadline };

// ── Constants ─────────────────────────────────────────────────────────────

/** Maximum number of reviews an instructor can have in-progress simultaneously. */
export const MAX_CONCURRENT_REVIEWS = 3;

// ── Status helpers ─────────────────────────────────────────────────────────

export type InstructorReviewStatus =
  | 'pending'
  | 'in_review'
  | 'delivered'
  | 'declined'
  | 'cancelled'
  | 'expired';

export const getInstructorStatus = (video: TrainingVideo): InstructorReviewStatus => {
  switch (video.status) {
    case 'pending_review': {
      const age = Date.now() - new Date(video.created_at).getTime();
      return age >= ACCEPTANCE_WINDOW_MS ? 'expired' : 'pending';
    }
    case 'under_review':
      return 'in_review';
    case 'reviewed':
      return 'delivered';
    case 'declined':
      return 'declined';
    case 'expired':
      return 'expired';
    case 'archived':
      return 'cancelled';
    default:
      return 'pending';
  }
};

// ── Error codes ────────────────────────────────────────────────────────────

export const ACCEPT_ERROR_LIMIT_REACHED = 'LIMIT_REACHED';

// ── Hook ──────────────────────────────────────────────────────────────────

interface UseInstructorReviewsReturn {
  reviews: TrainingVideo[];
  isLoading: boolean;
  error: Error | null;
  pendingCount: number;
  inReviewCount: number;
  deliveredCount: number;
  isAtLimit: boolean;
  acceptReview: (videoId: string) => void;
  declineReview: (videoId: string, practitionerId?: string) => void;
  acceptError: Error | null;
  declineError: Error | null;
  isAccepting: boolean;
  isDeclining: boolean;
  acceptingId: string | null;
  decliningId: string | null;
  resetAcceptError: () => void;
}

export const useInstructorReviews = (): UseInstructorReviewsReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── Query ────────────────────────────────────────────────────────────────

  const { data: rawReviews = [], isLoading, error } = useQuery<TrainingVideo[]>({
    queryKey: ['instructor-reviews', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return rollReviewRepository.fetchInstructorReviewVideos(user.id);
    },
    enabled: !!user?.id,
    refetchInterval: 60_000, // keep countdown reasonably fresh
  });

  // Sort: pending first (most urgent = oldest), then rest by created_at desc
  const reviews = [...rawReviews].sort((a, b) => {
    const aStatus = getInstructorStatus(a);
    const bStatus = getInstructorStatus(b);
    const aIsPending = aStatus === 'pending';
    const bIsPending = bStatus === 'pending';

    if (aIsPending && !bIsPending) return -1;
    if (!aIsPending && bIsPending) return 1;

    if (aIsPending && bIsPending) {
      // Most urgent (earliest deadline) first
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }

    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const pendingCount  = rawReviews.filter(v => getInstructorStatus(v) === 'pending').length;
  const inReviewCount = rawReviews.filter(v => v.status === 'under_review').length;
  const deliveredCount = rawReviews.filter(v => v.status === 'reviewed').length;
  const isAtLimit = inReviewCount >= MAX_CONCURRENT_REVIEWS;

  // ── Mutations ────────────────────────────────────────────────────────────

  const acceptMutation = useMutation<void, Error, string>({
    mutationFn: async (videoId) => {
      try {
        await rollReviewApi.acceptReview(videoId);
      } catch (err) {
        if ((err as any)?.code === ACCEPT_ERROR_LIMIT_REACHED) {
          throw Object.assign(
            new Error(ACCEPT_ERROR_LIMIT_REACHED),
            { code: ACCEPT_ERROR_LIMIT_REACHED }
          );
        }
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-reviews', user?.id] });
    },
  });

  const declineMutation = useMutation<void, Error, { videoId: string; practitionerId?: string }>({
    mutationFn: async ({ videoId, practitionerId }) => {
      await rollReviewApi.declineReview(videoId, practitionerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-reviews', user?.id] });
    },
  });

  return {
    reviews,
    isLoading,
    error: error as Error | null,
    pendingCount,
    inReviewCount,
    deliveredCount,
    isAtLimit,
    acceptReview:    acceptMutation.mutate,
    declineReview:   (videoId: string, practitionerId?: string) =>
                       declineMutation.mutate({ videoId, practitionerId }),
    acceptError:     acceptMutation.error,
    declineError:    declineMutation.error,
    isAccepting:     acceptMutation.isPending,
    isDeclining:     declineMutation.isPending,
    acceptingId:     acceptMutation.isPending ? (acceptMutation.variables ?? null) : null,
    decliningId:     declineMutation.isPending ? (declineMutation.variables?.videoId ?? null) : null,
    resetAcceptError: acceptMutation.reset,
  };
};
